// ui/src/components/commonparts/SearchDateRange/SearchDateRange.tsx
import type { Dispatch, SetStateAction } from 'react'
import React, { useEffect, useMemo, useState } from 'react'

import { Box, Stack, Typography } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { addDays } from 'date-fns'
import { normalizeDate } from 'utils/convertFromLocalDate'

import SelectTypeCode from '../SelectTypeCode/SelectTypeCode'

import type { TypeGroup } from '../SelectTypeCode/SelectTypeCode'
import type { Groups } from 'api-client'

interface SearchDateRangeProps {
  SearchDateRangeStart?: string | null
  SearchDateRangeEnd?: string | null
  isShortRange?: boolean
  setSearchDateRangeStartType: Dispatch<SetStateAction<number | null | undefined>>
  setSearchDateRangeEndType: Dispatch<SetStateAction<number | null | undefined>>
  setSearchDateRangeStartDate: Dispatch<SetStateAction<Date | null | undefined>>
  setSearchDateRangeEndDate: Dispatch<SetStateAction<Date | null | undefined>>
  setSearchWeekNum: Dispatch<SetStateAction<number | null | undefined>>
}

function SearchDateRange({
  SearchDateRangeStart,
  SearchDateRangeEnd,
  isShortRange = true,
  setSearchDateRangeStartType,
  setSearchDateRangeEndType,
  setSearchDateRangeStartDate,
  setSearchDateRangeEndDate,
  setSearchWeekNum,
}: SearchDateRangeProps) {
  const [startCode, setStartCode] = useState<string | null | undefined>(SearchDateRangeStart)
  const [endCode, setEndCode] = useState<string | null | undefined>(SearchDateRangeEnd)

  const [startGroupInfo, setStartGroupInfo] = useState<Groups | undefined>(undefined)
  const [endGroupInfo, setEndGroupInfo] = useState<Groups | undefined>(undefined)

  const { loginUserInfo } = useAuthContext()

  const startGroupKey = useMemo<keyof typeof TypeGroup>(
    () => (isShortRange ? 'search_date_range_start_short' : 'search_date_range_start'),
    [isShortRange]
  )

  useEffect(() => {
    const startInt = Number(startCode)
    const endInt = Number(endCode)

    setSearchDateRangeStartType(startInt)
    setSearchDateRangeEndType(endInt)
    setSearchWeekNum((-startInt) + endInt)

    const today = normalizeDate(loginUserInfo?.systemDate ?? Date.now())!
    today.setHours(0, 0, 0, 0)

    const startDate = addDays(today, startInt + 1)
    const endDate = addDays(today, endInt)

    setSearchDateRangeStartDate(startDate)
    setSearchDateRangeEndDate(endDate)
  }, [
    startCode,
    endCode,
    setSearchDateRangeStartType,
    setSearchDateRangeEndType,
    setSearchDateRangeStartDate,
    setSearchDateRangeEndDate,
    setSearchWeekNum,
  ])

  useEffect(() => {
    setStartCode(SearchDateRangeStart)
  }, [SearchDateRangeStart])
  useEffect(() => {
    setEndCode(SearchDateRangeEnd)
  }, [SearchDateRangeEnd])

  return (
  <Box sx={{ ml: 1 }}>
    <Stack direction="row" spacing={0.5} alignItems="flex-end">

      <SelectTypeCode
        typeGroup={startGroupKey}
        title="開始"
        typeCode={startCode}
        setTypeCode={setStartCode}
        setTypeCodeInfo={setStartGroupInfo}
        isRequired
      />
      <Typography variant="body1" sx={{ pb: 0.5, fontSize: 16 }}>
        ~
      </Typography>
      <SelectTypeCode
        typeGroup="search_date_range_end"
        title="終了"
        typeCode={endCode}
        setTypeCode={setEndCode}
        setTypeCodeInfo={setEndGroupInfo}
        isRequired
      />
    </Stack>
  </Box>
  )
}

export default SearchDateRange